import React from 'react';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { ServiceLocationInfo } from '../../types/firestore';
import ServiceCard from './ServiceCard';
import {
  getBestMatchingSpecialization,
  getServiceSmartMatchReason,
  type ServiceDiscoveryIssueType,
} from '../../utils/serviceDiscovery';
import type { SmartSearchSelection } from '../../utils/smartSearch';
import { resolveServiceAvailabilityStatus } from '../../utils/businessHours';

interface ServiceGridProps {
  services: ServiceLocationInfo[];
  className?: string;
  isLoading?: boolean;
  emptyTitle?: string;
  emptyMessage?: string;
  query?: string;
  smartSelection?: SmartSearchSelection | null;
  issueType?: ServiceDiscoveryIssueType;
}

const ServiceGrid: React.FC<ServiceGridProps> = ({
  services,
  className = '',
  isLoading = false,
  emptyTitle = 'Brak wynikow',
  emptyMessage = 'Nie znaleziono serwisow spelniajacych wybrane kryteria.',
  query = '',
  smartSelection = null,
  issueType = null,
}) => {
  const gridClassName = `grid min-w-0 grid-cols-1 gap-2.5 sm:gap-3 lg:grid-cols-2 2xl:grid-cols-3 ${className}`;

  if (isLoading) {
    return (
      <div
        className={gridClassName}
        role="status"
        aria-live="polite"
        aria-label="Ładowanie serwisów"
        data-testid="service-grid-loading"
      >
        {[0, 1, 2, 3, 4, 5].map((item) => (
          <div
            key={item}
            className="rs-surface-card grid min-h-52 min-w-0 grid-cols-[5.75rem_minmax(0,1fr)] overflow-hidden sm:grid-cols-[7rem_minmax(0,1fr)] lg:grid-cols-[8rem_minmax(0,1fr)]"
            aria-hidden="true"
            data-testid="service-card-skeleton"
          >
            <div className="rs-surface-workbench motion-safe:animate-pulse" />
            <div className="space-y-3 p-3 sm:p-3.5">
              <div className="h-5 w-3/4 rounded bg-[rgb(var(--rs-color-surface-card))] motion-safe:animate-pulse" />
              <div className="h-3 w-2/3 rounded bg-[rgb(var(--rs-color-surface-card))] motion-safe:animate-pulse" />
              <div className="h-3 w-full rounded bg-[rgb(var(--rs-color-surface-card))] motion-safe:animate-pulse" />
              <div className="h-3 w-5/6 rounded bg-[rgb(var(--rs-color-surface-card))] motion-safe:animate-pulse" />
              <div className="mt-auto h-10 w-full rounded bg-[rgb(var(--rs-color-surface-selected))] motion-safe:animate-pulse" />
            </div>
          </div>
        ))}
        <span className="sr-only">Pobieramy aktualną listę serwisów.</span>
      </div>
    );
  }

  if (!isLoading && services.length === 0) {
    return (
      <div className="rs-surface-empty py-8 text-center">
        <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg text-[rgb(var(--rs-color-text-muted))]">
          <BuildingOfficeIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <h3 className="mb-1.5 text-base font-medium text-[rgb(var(--rs-color-text-primary))]">
          {emptyTitle}
        </h3>
        <p className="text-sm text-[rgb(var(--rs-color-text-muted))]">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={gridClassName}>
      {services.map((service) => {
        const availabilityStatus = resolveServiceAvailabilityStatus(service);
        return (
          <ServiceCard
            key={service.uid}
            id={service.uid}
            name={service.serviceName}
            description={service.description}
            address={service.address || 'Brak adresu'}
            specializations={
              service.offeredCategories?.length
                ? service.offeredCategories
                : service.specializations
            }
            matchedSpecialization={getBestMatchingSpecialization(service, query)}
            matchReason={getServiceSmartMatchReason(service, smartSelection, issueType)}
            rating={service.averageRating}
            reviewCount={service.reviewsCount ?? service.numberOfRatings ?? 0}
            availabilityStatus={availabilityStatus}
            distance={service.distance}
            imageUrl={service.imageUrl}
            imageStoragePath={service.imageStoragePath}
            phoneNumber={service.phoneNumber}
            verifiedBusiness={service.isVerified}
            isTestData={service.isTestData}
            servicePricing={service.servicePricing}
          />
        );
      })}
    </div>
  );
};

export default ServiceGrid;
